import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { routerNavigatedAction } from '@ngrx/router-store';
import { Store, createSelector } from '@ngrx/store';
import {
  BulkEditCalendarEventGQL,
  BulkEditCalendarEventMutationVariables,
  FullCalendarEventFragment,
  RemoveCalendarEventGQL,
  ViewDatesGQL,
  ViewDatesQueryVariables,
} from 'graphql.generated';
import { ConfirmationService } from 'primeng/api';
import { catchError, concatMap, exhaustMap, map, mergeMap, tap, of, filter, switchMap, debounceTime, merge, take } from 'rxjs';

import { EventStatuses } from 'src/app/schedules/dispatch/store/dispatch.reducer';
import { DetailsHelperService } from 'src/app/services/details-helper.service';
import { DocumentHelperService } from "src/app/services/document-helper.service";
import { EventHelperService } from 'src/app/services/event-helper.service';
import { PermissionService } from 'src/app/services/permission.service';
import { dateMapHasRange, getMonthRange } from 'src/app/utilities/calendar.util';

import { dayjs } from '../../../core/dayjs/dayjs';
import { zonesFeature } from '../../../core/zones/zones.reducer';
import { JobCreateCustomerActions } from '../../job-create/job-create-customer/job-create-customer.actions';
import { JobFormActionButtonActions } from '../../job-create/job-form-action-button/job-form-action-button.actions';
import { JobToolActions } from '../../job-tool.actions';
import { selectJob, selectJobId } from '../../job-tool.reducer';

import { WorkOrdersActions } from '../../job-workorders/job-workorders.actions';
import { jobWorkordersFeature } from '../../job-workorders/job-workorders.feature';


import { OverviewTimelineActions } from './overview-timeline.actions';
import { overviewTimelineFeature } from './overview-timeline.feature';

const updateEventStatusSuccessMessageMap: Partial<Record<EventStatuses, string>> = {
    [EventStatuses.Cancelled]: 'Event cancelled',
    [EventStatuses.Pending]: 'Event restored',
    [EventStatuses.Confirmed]: 'Event confirmed',
    [EventStatuses.Completed]: 'Event marked completed'
}

export const updateEventStatus = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
) => {
    return actions$.pipe(
      ofType(
        OverviewTimelineActions.confirmEventMenuItemSelected,
        OverviewTimelineActions.completeEventMenuItemSelected,
        OverviewTimelineActions.confirmCancelEventButtonClicked,
        OverviewTimelineActions.confirmUndoCancelButtonClicked,
      ),
      mergeMap((action) => of(action).pipe(
        map((action): EventStatuses => {
          switch (action.type) {
            case OverviewTimelineActions.confirmEventMenuItemSelected.type:
              return EventStatuses.Confirmed;
            case OverviewTimelineActions.completeEventMenuItemSelected.type:
              return EventStatuses.Completed;
            case OverviewTimelineActions.confirmCancelEventButtonClicked.type:
              return EventStatuses.Cancelled;
            case OverviewTimelineActions.confirmUndoCancelButtonClicked.type:
              return EventStatuses.Pending;
          }
        }),
        concatLatestFrom(() => [
          store.select(overviewTimelineFeature.selectTimelineActionsEventId),
          store.select(selectJobId),
        ]),
        map(([ status, eventId, jobId ]): {
          vars: BulkEditCalendarEventMutationVariables;
          jobId: string;
          status: EventStatuses;
        } => {
          return {
            vars: { edits: [ { id: eventId, edit: { status } } ] },
            jobId,
            status,
          };
        }),
        exhaustMap(({ vars, status, jobId }) => bulkEditCalendarEventGQL.mutate(vars).pipe(
          map((res) => OverviewTimelineActions.updateEventStatusSuccess({
            updatedEvent: res.data.bulkEditCalendarEvent.events[0],
            jobId,
            customSuccessMessage: updateEventStatusSuccessMessageMap[status]
          })),
        )),
        catchError((error) => of(OverviewTimelineActions.updateEventStatusError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const promoteEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  bulkEditCalendarEventGQL = inject(BulkEditCalendarEventGQL),
) => {
    return actions$.pipe(
      ofType(WorkOrdersActions.promoteEventMenuItemClicked),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(selectJob),
          store.select(jobWorkordersFeature.selectWorkordersEditor)
        ]),
        map(([ _action, job, { actionsMenuEventId } ]) => {

          const event = job.events.find((e) => e.id === actionsMenuEventId);

          return {
            event,
            jobId: job.id,
            promoteToStatus: event?.status === 'booked' ? EventStatuses.Confirmed : EventStatuses.Completed,
          }
        }),
        filter(({ event }) => event && [ 'booked', 'confirmed' ].includes(event.status)),
        map(({ event, jobId, promoteToStatus }): {
          vars: BulkEditCalendarEventMutationVariables;
          jobId: string;
          promoteToStatus: EventStatuses,
        } => {
          return {
            vars: { edits: [ { id: event.id, edit: { status: promoteToStatus } } ] },
            jobId,
            promoteToStatus,
          };
        }),
        exhaustMap(({ vars, jobId, promoteToStatus }) => bulkEditCalendarEventGQL.mutate(vars).pipe(
          map((res) => OverviewTimelineActions.updateEventStatusSuccess({
            updatedEvent: res.data.bulkEditCalendarEvent.events[0],
            jobId,
            customSuccessMessage: updateEventStatusSuccessMessageMap[promoteToStatus]
          })),
        )),
        catchError((error) => of(OverviewTimelineActions.updateEventStatusError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const openCreateInvoiceDialog = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  documentHelper = inject(DocumentHelperService),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.promoteEventButtonClicked),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => store.select(selectJob)),
        map(([ { eventId }, job ]) => ({
          event: job.events.find((e) => e.id === eventId),
          jobId: job.id,
        })),
        filter(({ event }) => event.status === 'completed'),
        tap(({ jobId }) => documentHelper.openCreateInvoiceDialog(jobId)),
      )),
    );
}, { functional: true, dispatch: false });

export const openEventInRightPanel = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  detailsHelper = inject(DetailsHelperService),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.moreInfoMenuItemSelected),
      concatLatestFrom(() => store.select(overviewTimelineFeature.selectTimelineActionsEventId)),
      tap(([ _action, eventId ]) => detailsHelper.open('calendar-event', { id: eventId })),
    );
}, { functional: true, dispatch: false });

export const confirmDeleteEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.deleteMenuItemSelected),
      concatLatestFrom(() => store.select(createSelector(
        selectJob,
        overviewTimelineFeature.selectTimelineActionsEventId,
        (job, eventId) => job.events.find((e) => e.id === eventId)?.title,
      ))),
      map(([
        _action,
        title,
      ]) => title ? `Are you sure you want to delete event: ${title}?` : 'Are you sure you want to delete this event?'),
      tap((message: string) => confirmationService.confirm({
        message,
        header: 'Delete Confirmation',
        icon: 'pi pi-trash',
        acceptButtonStyleClass: "p-button-danger",
        rejectButtonStyleClass: "p-button-text",
        acceptLabel: 'Delete this Event',
        acceptIcon: 'pi pi-trash',
        rejectLabel: `Don't Delete this Event`,
        rejectIcon: 'pi pi-times',
        accept: () => store.dispatch(OverviewTimelineActions.confirmDeleteEventButtonClicked()),
      })),
    );
}, { functional: true, dispatch: false });

export const confirmUndoCancel = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  confirmationService = inject(ConfirmationService),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.undoCancelMenuItemSelected),
      tap(() => confirmationService.confirm({
        header: 'Undo Cancel?',
        message: `This will add the event back into the schedule ` +
          `but it will not actually book the event nor will it check if there are any conflicting events scheduled for the same time. ` +
          `Please make sure there are no conflicting events, then book the event manually.`,
        acceptLabel: 'Undo Cancel',
        acceptIcon: 'pi pi-undo',
        rejectLabel: 'Nevermind',
        rejectIcon: 'pi pi-times',
        accept: () => store.dispatch(OverviewTimelineActions.confirmUndoCancelButtonClicked()),
      })),
    );
}, { functional: true, dispatch: false });

export const deleteEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  removeCalendarEventGQL = inject(RemoveCalendarEventGQL),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.confirmDeleteEventButtonClicked),
      mergeMap((action) => of(action).pipe(
        concatLatestFrom(() => [
          store.select(overviewTimelineFeature.selectTimelineActionsEventId),
          store.select(selectJobId),
        ]),
        exhaustMap(([ _action, eventId, jobId ]) => removeCalendarEventGQL.mutate({ ids: [ eventId ]}).pipe(
          map(() => OverviewTimelineActions.deleteEventSuccess({
            eventId,
            jobId,
          })),
        )),
        catchError((error) => of(OverviewTimelineActions.deleteEventError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });

export const openEventInWorkordersTab = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  router = inject(Router),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.goToEventButtonClicked),
      concatLatestFrom(() => store.select(createSelector(
        selectJobId,
        (jobId) => `/jobs/${jobId}/workorders`,
      ))),
      tap(([ { eventId: workorderId }, url ]) => router.navigate(
        [ url ],
        {
          queryParams: { workorderId },
          queryParamsHandling: 'merge',
        },
      )),
    );
}, { functional: true, dispatch: false });

export const uncancelEvent = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  eventHelper = inject(EventHelperService),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.uncancelEventButtonClicked),
      concatLatestFrom(({ eventId }) => store.select(createSelector(
        selectJob,
        (job) => job?.events?.find((e) => e.id === eventId),
      ))),
      filter(([ _action, event ]) => Boolean(event)),
      tap(([ _action, event ]) => eventHelper.uncancelEvent(event as FullCalendarEventFragment)),
    );
}, { functional: true, dispatch: false });

export const syncNavigatorStep = createEffect(() => {
  const actions$ = inject(Actions);
  return actions$.pipe(
    ofType(OverviewTimelineActions.updateEventStatusSuccess),
    // Just as placeholder, ideally we listen to
    // quickJsActions.navigatorStepConfirmed
    // This is a placeholder for the actual sync effect
    tap(() => {})
  );
}, { functional: true, dispatch: false });

export const triggerFetchViewDates = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
) => {

    const timelineChanged$ = actions$.pipe(
      ofType(
        OverviewTimelineActions.availabilityScheduledInitialized,
        JobToolActions.jobLoaded,
        JobToolActions.jobDataReset,
        JobCreateCustomerActions.updateCustomerDataSuccess,
        JobFormActionButtonActions.updateJobSuccess,
      ),
      concatLatestFrom(() => store.select(createSelector(
        overviewTimelineFeature.selectTimeline,
        (timeline) => {
          if (Boolean(timeline) && Array.isArray(timeline) && timeline.length > 0) {
            return dayjs(timeline[0]);
          }
          return dayjs();
        }
      ))),
      map(([ _action, timelineStart ]) => getMonthRange(timelineStart)),
    );

    const newOpportunityEntered$ = actions$.pipe(
      ofType(routerNavigatedAction),
      filter(({ payload: { event: { urlAfterRedirects } } }) => (
        urlAfterRedirects.startsWith('/jobs/new')
      )),
      map(() => getMonthRange()),
    );

    const monthChanged$ = actions$.pipe(
      ofType(OverviewTimelineActions.monthChanged),
      map(({ month, year}) => dayjs().year(year).month(month - 1)),
      map((current) => getMonthRange(current)),
    );

    return merge(timelineChanged$, newOpportunityEntered$, monthChanged$).pipe(
      debounceTime(10),
      switchMap((range) => store.select(zonesFeature.selectCurrentAppZone).pipe(
        filter(Boolean),
        take(1),
        map(() => range),
      )),
      concatLatestFrom(() => store.select(overviewTimelineFeature.selectViewDatesMap)),
      filter(([ { startDate, endDate }, viewDatesMap ]) => !dateMapHasRange(viewDatesMap, startDate, endDate)),
      map(([ { startDate, endDate } ]) => OverviewTimelineActions.fetchViewDatesStarted({ startDate, endDate })),
    );
}, { functional: true, dispatch: true });

export const fetchViewDates = createEffect((
  actions$ = inject(Actions),
  store = inject(Store),
  viewDatesGQL = inject(ViewDatesGQL),
  permissionHandler = inject(PermissionService),
) => {
    return actions$.pipe(
      ofType(OverviewTimelineActions.fetchViewDatesStarted),
      // Pair the trigger with the first permission emission; concatLatestFrom
      // drops the action when watchPermission hasn't emitted yet, which would
      // leave view dates empty until the next trigger.
      concatMap((action) => permissionHandler.watchPermission('find.viewDates').pipe(
        take(1),
        map((hasViewDates) => [ action, hasViewDates ] as const),
      )),
      filter(([, hasViewDates]) => hasViewDates),
      concatLatestFrom(() => store.select(zonesFeature.selectCurrentAppZone)),
      filter(([, zone]) => Boolean(zone)),
      map(([[ { startDate, endDate }, _hasViewDates], currentZone]): ViewDatesQueryVariables => ({
        startDate,
        endDate,
        zone: currentZone.id,
        includeEvents: false,
        prophet: true,
      })),
      // `switchMap`, not `exhaustMap`: a later missing range arriving while a
      // fetch is active must supersede it, or that range never loads until the
      // next trigger.
      switchMap((variables) => viewDatesGQL.fetch(variables).pipe(
        map((res) => OverviewTimelineActions.fetchViewDatesSuccess({
          viewDates: res.data.viewDates,
        })),
        catchError((error) => of(OverviewTimelineActions.fetchViewDatesError({ error }))),
      )),
    );
}, { functional: true, dispatch: true });
